import React from "react";
import {
  Button,
  FormFieldSet,
  LoopFieldsSet,
  PageTitle,
} from "../../../../../components";
import { v4 as uuidv4 } from "uuid";
import useIncentiveSetting from "./useIncentiveSetting";

const BenchSaleSetting = ({ onClose }) => {
  const initialFormData = {
    quarterlyTarget: "",
    yearlyTarget: "",
    generalPct: "",
    q1Pct: "",
    fiscalYearPct: "",
    placementIncentiveAmount: "",
    benchSaleSettingDetails: [
      {
        id: uuidv4(),
        invoicePaidRequired: "",
        minimumStayPeriod: "",
        incentiveAmount: "",
      },
    ],
  };

  const validationConfig = {
    quarterlyTarget: { type: "text", number: true, required: true },
    yearlyTarget: { type: "text", number: true, required: true },
    generalPct: { type: "text", number: true, required: true },
    q1Pct: { type: "text", number: true, required: true },
    fiscalYearPct: { type: "text", number: true, required: true },
    placementIncentiveAmount: { type: "text", number: true, required: true },
    benchSaleSettingDetails: [
      {
        invoicePaidRequired: { type: "select", required: true },
        minimumStayPeriod: { type: "select", required: true },
        incentiveAmount: { type: "text", number: true, required: true },
      },
    ],
  };

  const {
    formData,
    errors,
    handleChange,
    loading,
    addItem,
    removeItem,
    saveSettings,
  } = useIncentiveSetting({
      settingsKey: "INCENTIVE_BENCH",
      listFieldName: "benchSaleSettingDetails",
      initialFormData,
      validationConfig,
      onClose
  });

  return (
    <div className="flex flex-col mt-5">
        <PageTitle
        title="Targets"
        tag="p"
        className="text-gray-400 text-sm mb-2"
        />
        <div className="grid xs:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
            <FormFieldSet
                title="General Rate (Baseline)"
                placeholder="Enter rate/amount"
                value={formData.generalPct || ""}
                onChange={(value) => handleChange("generalPct", value)}
                error={errors.generalPct}
                required={true}
            />
            <FormFieldSet
                title="Quarterly Rate (Tier 1)"
                placeholder="Enter rate/amount"
                value={formData.q1Pct || ""}
                onChange={(value) => handleChange("q1Pct", value)}
                error={errors.q1Pct}
                required={true}
            />
            <FormFieldSet
                title="Yearly Rate (Tier 2)"
                placeholder="Enter rate/amount"
                value={formData.fiscalYearPct || ""}
                onChange={(value) => handleChange("fiscalYearPct", value)}
                error={errors.fiscalYearPct}
                required={true}
            />
        </div>
        <div className="grid xs:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
            <FormFieldSet
                title="Quarterly Target (Placement Count)"
                placeholder="Enter count"
                value={formData.quarterlyTarget || ""}
                onChange={(value) => handleChange("quarterlyTarget", value)}
                error={errors.quarterlyTarget}
                required={true}
            />
            <FormFieldSet
                title="Yearly Target (Placement Count)"
                placeholder="Enter count"
                value={formData.yearlyTarget || ""}
                onChange={(value) => handleChange("yearlyTarget", value)}
                error={errors.yearlyTarget}
                required={true}
            />
        </div>

      <PageTitle
        title="One-Time Placement Incentive"
        tag="p"
        className="text-gray-400 text-sm"
      />
      <div className="grid xs:grid-cols-2 xl:grid-cols-3 gap-4 mt-2 mr-8">
        <FormFieldSet
          title="Placement Incentive Amount"
          placeholder="Enter amount"
          value={formData.placementIncentiveAmount || ""}
          onChange={(value) => handleChange("placementIncentiveAmount", value)}
          error={errors.placementIncentiveAmount}
          required={true}
        />
      </div>
      <div className="flex flex-col gap-4">
        <LoopFieldsSet
          title="Incentive Rules"
          titleConfig={{ tag: "p", className: "text-gray-400 text-sm" }}
          data={formData.benchSaleSettingDetails}
          onAdd={addItem}
          onRemove={removeItem}
          minItems={1}
          maxItems={5}
          addButtonTitle="Add More"
          gridCols="grid-cols-[1fr_30px] gap-1!"
          renderFields={(item, index) => (
            <>
              <div className="flex flex-col gap-4 p-0">
                <div className="grid xs:grid-cols-2 xl:grid-cols-3 gap-4">
                  <FormFieldSet
                    title="Invoice Paid Required"
                    placeholder="Select"
                    type="select"
                    options={[
                      { label: "Yes", value: "yes" },
                      { label: "No", value: "no" },
                    ]}
                    value={item.invoicePaidRequired || ""}
                    onChange={(value) =>
                      handleChange(
                        `benchSaleSettingDetails[${index}].invoicePaidRequired`,
                        value
                      )
                    }
                    error={
                      errors.benchSaleSettingDetails?.[index]
                        ?.invoicePaidRequired
                    }
                    required={true}
                  />
                  <FormFieldSet
                    title="Minimum Stay Period"
                    placeholder="Select"
                    type="select"
                    options={[
                      { label: "No restriction", value: "0" },
                      { label: "1 month", value: "1m" },
                      { label: "2 months", value: "2m" },
                      { label: "3 months", value: "3m" },
                      { label: "6 months", value: "6m" },
                      { label: "12 months", value: "12m" },
                    ]}
                    value={item.minimumStayPeriod || ""}
                    onChange={(value) =>
                      handleChange(
                        `benchSaleSettingDetails[${index}].minimumStayPeriod`,
                        value
                      )
                    }
                    error={
                      errors.benchSaleSettingDetails?.[index]?.minimumStayPeriod
                    }
                    required={true}
                  />
                  <FormFieldSet
                    title="Incentive Amount($)"
                    placeholder="Enter amount"
                    value={item.incentiveAmount || ""}
                    onChange={(value) =>
                      handleChange(
                        `benchSaleSettingDetails[${index}].incentiveAmount`,
                        value
                      )
                    }
                    error={
                      errors.benchSaleSettingDetails?.[index]?.incentiveAmount
                    }
                    required={true}
                  />
                </div>
              </div>
            </>
          )}
        />

        <div className="flex items-center justify-end gap-3">
          <Button title="Cancel" variant="outlined" onClick={onClose} />
          <Button title={loading ? "Saving..." : "Save"} onClick={saveSettings} disabled={loading} />
        </div>
      </div>
    </div>
  );
};

export default BenchSaleSetting;
